import { format } from "date-fns";
import { Values } from "../../../../context";
import { DATE_FORMAT } from "@constants";
import { getAdditionalFormKeyFromApplication } from "../../../../utils";

type Payload = {
  requestorPrimaryPhone: string;
  requestorNtLogin: string;
  requestType: "New Access" | "Modify Access" | "Revoke Access";
  requestTypeId: 1 | 2 | 3;
  revokeType: "Resignation/Termination" | "Unnecessary Access" | null;
  effectiveDate: string; // DATE_FORMAT
  justification: string;
  attestation: string;
  draftKey: string;
  requests: Request[];
};

type Request = {
  isAdvance: boolean; //is before employee started
  startDate: string | null; //start date for employee if isAdvance // DATE_FORMAT
  accessDetail: string; //if app.isManual then "", else what the user typed in for "access details"
  categoryId: number; //app.categoryId,
  deviceTypeAppId: number; //if app.isManual and "infrastructure application" dropdown had data, then the id from the selected option. Otherwise, 0
  deviceTypeId: number; //app.id,
  environmentId: number; //if !app.isManual then environment.id else 0
  role: string; //if app.isManual, then "". else role.name
  roleId: number; //if app.isManual, then 0, if !app.isManual, then role.id
  subCategoryId: number; //app.subCategoryId
  subCategoryName: string; //app.name
  systemTypeId: number; //app.systemTypeId
  userEmail: string;
  userId: string;
  userName: string;
  infrastructureDetails?: {
    environmentId: number; // infra app env id
    role: string;
    accessDetails: string;
    subCategoryName: string;
  }[];
  form?: {
    [key: string]: any;
  };
};

function getRequestType(type: Values["type"]): Payload["requestType"] {
  switch (type) {
    case "new":
      return "New Access";
    case "modify":
      return "Modify Access";
    case "revoke":
      return "Revoke Access";
    default:
      throw new Error("Missing request type");
  }
}

function getRequestTypeId(type: Values["type"]): Payload["requestTypeId"] {
  switch (type) {
    case "new":
      return 1;
    case "modify":
      return 2;
    case "revoke":
      return 3;
    default:
      throw new Error("Missing request type");
  }
}

function getEffectiveDate(date: Values["date"]): Payload["effectiveDate"] {
  if (date === undefined) {
    throw new Error("");
  }
  return format(date, DATE_FORMAT);
}

function getJustification(
  justification: Values["justification"]
): Payload["justification"] {
  if (justification === undefined) {
    throw new Error("");
  }
  return justification;
}

function getAttestation(
  attestation: Values["attestation"]
): Payload["attestation"] {
  return attestation || "";
}

function getRevokeType(type: Values["revoke_type"]): Payload["revokeType"] {
  switch (type) {
    case "termination":
      return "Resignation/Termination";
    case "unnecessary":
      return "Unnecessary Access";
    default:
      return null;
  }
}

function getRequests(
  users: Values["users"],
  systems: Values["systems"]
): Payload["requests"] {
  return users.reduce<Request[]>((requests, user) => {
    return [
      ...requests,
      ...systems.reduce<Request[]>((requests, system) => {
        return [
          ...requests,
          ...system.accesses.reduce<Request[]>((requests, access) => {
            // Might need to reuse request if system.application.manual
            // and requests.find(request => request.deviceTypeAppId === access.infrastructureApplication?.id)
            // return requests with updated request

            if (system.application.manual) {
              const request = requests.find(
                (request) =>
                  request.deviceTypeAppId ===
                  access.infrastructureApplication?.id
              );

              if (request) {
                request.infrastructureDetails = (
                  request.infrastructureDetails || []
                ).concat({
                  environmentId: access.environment?.id!, // forced
                  role: access.role?.name!, // forced
                  accessDetails: access.accessDetails!, // forced
                  subCategoryName: access.subCategoryName!, // forced
                });

                return requests;
              }
            }

            return [
              ...requests,
              Object.assign<Request, Partial<Request>, Partial<Request>>(
                {
                  isAdvance: !!user.start_date,
                  startDate: user.start_date
                    ? format(user.start_date, DATE_FORMAT)
                    : null,
                  accessDetail: system.application.manual
                    ? ""
                    : access.accessDetails!, // forced
                  categoryId: system.application.categoryId,
                  deviceTypeAppId: system.application.manual
                    ? access.infrastructureApplication?.id!
                    : 0, // forced
                  deviceTypeId: system.application.id,
                  environmentId:
                    system.application.manual || system.application.lz
                      ? 0
                      : access.environment?.id!, // forced
                  role: system.application.manual ? "" : access.role?.name!, // forced
                  roleId: system.application.manual ? 0 : access.role?.id!, // forced
                  subCategoryId: system.application.id, // app.subCategoryId ??
                  subCategoryName: system.application.name,
                  systemTypeId: system.application.systemTypeId,
                  userEmail: user.email,
                  userId: user.id,
                  userName: user.name,
                },
                system.application.manual
                  ? {
                      infrastructureDetails: [
                        {
                          environmentId: access.environment?.id!, // forced
                          role: access.role?.name!, // forced
                          accessDetails: access.accessDetails!, // forced
                          subCategoryName: access.subCategoryName!, // forced
                        },
                      ],
                    }
                  : {},
                system.application.additionalForm && access.form
                  ? {
                      form: {
                        [getAdditionalFormKeyFromApplication(
                          system.application.additionalForm
                        ) as string]: access.form,
                      },
                    }
                  : {}
              ),
            ];
          }, []),
        ];
      }, []),
    ];
  }, []);
}

export function getRequestPayload(values: Values): Payload {
  return {
    requestorPrimaryPhone: "",
    requestorNtLogin: "",
    requestType: getRequestType(values.type),
    requestTypeId: getRequestTypeId(values.type),
    effectiveDate: getEffectiveDate(values.date),
    justification: getJustification(values.justification),
    attestation: getAttestation(values.attestation),
    draftKey: values.draft_key || "",
    revokeType: getRevokeType(values.revoke_type),
    requests: getRequests(values.users, values.systems),
  };
}
